import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ExportStatus } from '@api-types/enums/export-status.enum';
import { FilterValue } from '@api-types/types/filter-value.type';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import {
  FormFieldComponent,
  FormInputNumberComponent,
  PmDialogComponent,
  PmTabsGroupComponent,
  SelectTabOption,
} from '@libs/ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConsensusExportService } from 'demand-forecasting/store/consensus/consensus-export.service';
import { ConsensusExportStateService } from 'demand-forecasting/store/consensus/consensus-export-state.service';
import { ConsensusExportListTabComponent } from './consensus-export-list-tab.component';

export enum ConsensusExportTab {
  NEW = 'new',
  LIST = 'list',
}

export type ConsensusExportScope = 'complete' | 'visible';
export type ConsensusExportFormatChoice = 'CSV' | 'XLSX';

/** Default decimal places in the NEW tab — matches the grid's 2-decimal display. */
const DEFAULT_DECIMAL_DIGITS = 2;
const MAX_DECIMAL_DIGITS = 30;
const MIN_DECIMAL_DIGITS = 0;

export interface ConsensusExportDialogData {
  configurationId: string;
  consensusTemplateId: string;
  /** Callback for the "Export what you see" path — wired to the AG-Grid api
   *  by the page that opens the dialog. Receives the chosen format. */
  exportVisible?: (format: ConsensusExportFormatChoice) => void;
  initialTab?: ConsensusExportTab;
  highlightExportId?: string;
  /** Ordered planning hierarchy (selectPlanningHierarchy) for the aggregation
   *  level picker. Each prefix is a selectable "summarize to this level". */
  aggregationLevels?: string[];
  /** Active grid filters, snapshotted at open, for the "apply filters" option. */
  appliedFilters?: {
    rawFilters: Record<string, FilterValue[]>;
    filterCombinationIds: string[];
  };
  /**
   * First month (`yyyy-MM`) of the picked range when it reaches before the
   * forecast horizon, so the export carries the same Ist columns as the table.
   */
  historyStartMonth?: string;
}

@Component({
  templateUrl: './consensus-export-dialog.component.html',
  imports: [
    PmDialogComponent,
    PmTabsGroupComponent,
    FormFieldComponent,
    FormInputNumberComponent,
    ConsensusExportListTabComponent,
    MatRadioModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
})
export class ConsensusExportDialogComponent implements OnInit {
  protected readonly dialogRef = inject(
    MatDialogRef<ConsensusExportDialogComponent>,
  );
  protected readonly dialogData =
    inject<ConsensusExportDialogData>(MAT_DIALOG_DATA);

  private readonly consensusExportService = inject(ConsensusExportService);
  private readonly stateService = inject(ConsensusExportStateService);
  private readonly translate = inject(TranslateService);

  protected readonly ConsensusExportTab = ConsensusExportTab;

  protected readonly activeTab = signal<ConsensusExportTab>(
    this.dialogData.initialTab ?? ConsensusExportTab.NEW,
  );

  /** NEW tab radios. */
  protected readonly scope = signal<ConsensusExportScope>('complete');
  protected readonly format = signal<ConsensusExportFormatChoice>('XLSX');

  /** NEW tab: value-cell decimal places (0–30), mirrors the forecast export.
   *  Reactive control so it uses the shared pm-form-input-number counter. */
  protected readonly decimalDigitsControl = new FormControl<number>(
    DEFAULT_DECIMAL_DIGITS,
    { nonNullable: true },
  );
  protected readonly minDecimalDigits = MIN_DECIMAL_DIGITS;
  protected readonly maxDecimalDigits = MAX_DECIMAL_DIGITS;

  /** Ordered planning hierarchy levels available for an aggregated export. */
  protected readonly aggregationLevels: string[] =
    this.dialogData.aggregationLevels ?? [];

  /** Level radio options — `name` is the dimension shown at that level; `columns`
   *  is the cumulative prefix (level i groups by columns[0..i]). */
  protected readonly aggregationOptions = this.aggregationLevels.map(
    (name, i) => ({
      name,
      columns: this.aggregationLevels.slice(0, i + 1),
    }),
  );

  /** Aggregation radio selection: 'leaf' (default) or a level index as string. */
  protected readonly aggregationSelection = signal<string>('leaf');

  /** True when a dimension filter is active in the grid (snapshot at open). */
  protected readonly hasActiveFilter = this.deriveHasActiveFilter();

  /** Whether to scope the export to the active filter (default on when active). */
  protected readonly applyFilters = signal(true);

  /** LIST tab selection. */
  protected readonly selectedExportId = signal<string | undefined>(
    this.dialogData.highlightExportId,
  );

  protected readonly tabOptions: SelectTabOption[] = [
    {
      value: ConsensusExportTab.NEW,
      label: 'CONSENSUS.EXPORT.TAB_NEW',
      disabled: false,
    },
    {
      value: ConsensusExportTab.LIST,
      label: 'CONSENSUS.EXPORT.TAB_LIST',
      disabled: false,
    },
  ];

  protected readonly primaryCtaText = computed(() => {
    if (this.activeTab() === ConsensusExportTab.LIST) {
      return 'COMMON.DOWNLOAD';
    }
    return this.scope() === 'visible'
      ? 'COMMON.DOWNLOAD'
      : 'CONSENSUS.EXPORT.START';
  });

  protected readonly primaryCtaDisabled = computed(() => {
    if (this.activeTab() === ConsensusExportTab.LIST) {
      return !this.selectedExportId();
    }
    return this.consensusExportService.startExportLoading();
  });

  /** Only the server-side "Start export" path runs async; show the spinner there. */
  protected readonly primaryCtaLoading = computed(
    () =>
      this.activeTab() === ConsensusExportTab.NEW &&
      this.scope() === 'complete' &&
      this.consensusExportService.startExportLoading(),
  );

  /** Explain that the export runs in the background and appears under Downloads. */
  protected readonly primaryCtaTooltip = computed(() =>
    this.activeTab() === ConsensusExportTab.NEW && this.scope() === 'complete'
      ? this.translate.instant('CONSENSUS.EXPORT.START_TOOLTIP')
      : undefined,
  );

  protected readonly primaryCtaButtonConfig = computed(() => ({
    text: this.primaryCtaText(),
    disabled: this.primaryCtaDisabled(),
    dataTestid: 'consensus-export-primary-cta',
    isLoading: this.primaryCtaLoading(),
    tooltip: this.primaryCtaTooltip(),
    handleClick: () => this.handlePrimaryCtaClick(),
  }));

  constructor() {
    // Drop any completion observed before this dialog opened, so the auto-select
    // effect only fires for exports that finish while it is on the LIST tab —
    // otherwise a stale id from an earlier completion could select the wrong row.
    this.stateService.lastCompletedExportId.set(undefined);

    // When an export completes while this dialog is open on the LIST tab,
    // auto-select the finished row for one-click download. Only fires when
    // nothing is manually selected, and consumes the trigger so it never
    // overrides a later manual change.
    effect(() => {
      if (this.activeTab() !== ConsensusExportTab.LIST) return;
      if (this.selectedExportId() !== undefined) return;
      const completedId = this.stateService.lastCompletedExportId();
      if (!completedId) return;
      const finished = (this.stateService.exports.value() ?? []).some(
        (e) => e.id === completedId && e.status === ExportStatus.COMPLETED,
      );
      if (finished) {
        this.selectedExportId.set(completedId);
        this.stateService.lastCompletedExportId.set(undefined);
      }
    });
  }

  ngOnInit(): void {
    // ensure the list is fresh whenever the dialog opens on the LIST tab
    if (this.activeTab() === ConsensusExportTab.LIST) {
      this.stateService.exports.reload();
    }
  }

  protected setActiveTab(tab: string): void {
    this.activeTab.set(tab as ConsensusExportTab);
  }

  /** The chosen aggregation prefix, or undefined for a leaf (full-detail) export. */
  private resolveAggregationColumns(): string[] | undefined {
    const selection = this.aggregationSelection();
    if (selection === 'leaf') return undefined;
    return this.aggregationOptions[Number(selection)]?.columns;
  }

  private deriveHasActiveFilter(): boolean {
    const filters = this.dialogData.appliedFilters;
    if (!filters) return false;
    const hasRawFilter = Object.values(filters.rawFilters ?? {}).some(
      (values) => values.length > 0,
    );
    return hasRawFilter || (filters.filterCombinationIds?.length ?? 0) > 0;
  }

  /** Filter payload for the export — only when a filter is active AND opted in. */
  private resolveFilterPayload(): {
    rawFilters?: Record<string, FilterValue[]>;
    filterCombinationIds?: string[];
  } {
    if (!this.hasActiveFilter || !this.applyFilters()) return {};
    return {
      rawFilters: this.dialogData.appliedFilters?.rawFilters,
      filterCombinationIds:
        this.dialogData.appliedFilters?.filterCombinationIds,
    };
  }

  /** Thin dispatcher — the actual work lives in the per-mode handlers below. */
  protected handlePrimaryCtaClick(): void {
    if (this.activeTab() === ConsensusExportTab.LIST) {
      this.handleListDownload();
      return;
    }
    if (this.scope() === 'visible') {
      this.handleVisibleExport();
      return;
    }
    this.handleServerExport();
  }

  /** LIST tab: download the selected export, then close. */
  private handleListDownload(): void {
    const id = this.selectedExportId();
    if (!id) return;
    this.consensusExportService
      .downloadSelectedExport(id)
      .then(() => this.dialogRef.close());
  }

  /** NEW tab, scope 'visible': hand off to the grid-side export, then close. */
  private handleVisibleExport(): void {
    this.dialogData.exportVisible?.(this.format());
    this.dialogRef.close();
  }

  /** NEW tab, scope 'complete': start the server-side flow and switch to LIST. */
  private handleServerExport(): void {
    this.consensusExportService
      .startExport({
        configurationId: this.dialogData.configurationId,
        consensusTemplateId: this.dialogData.consensusTemplateId,
        format: this.format(),
        decimalDigits: this.decimalDigitsControl.value,
        aggregationColumns: this.resolveAggregationColumns(),
        historyStartMonth: this.dialogData.historyStartMonth,
        ...this.resolveFilterPayload(),
      })
      .subscribe({
        next: () => {
          this.activeTab.set(ConsensusExportTab.LIST);
        },
        error: () => {
          // toast already shown by the service
        },
      });
  }
}
